/**
 * Header — Sprint 1.5 topbar restructure.
 *
 *   ┌───────────────────────────────────────────────────────────────────────┐
 *   │ [Logo] [Entity▾]   [✨ Ask Nexus anything…    AI ⌘K]   [?] [🔔] [👤▾] │
 *   └───────────────────────────────────────────────────────────────────────┘
 *
 * What changed in Sprint 1.5 (2026-05-28):
 *   - ADDED  Ask Nexus search bar (center, wide, ⌘K shortcut, "AI" hint pill)
 *   - ADDED  Lifebuoy Help icon (links to /help)
 *   - ADDED  Avatar button opens UserDropdown (10-item menu)
 *   - REMOVED Sign Out button (moved → UserDropdown last item, danger style)
 *   - REMOVED Moon-icon theme toggle (moved → SidebarUserFooter 3-state row)
 *
 * Why: the prior topbar had a 14px right-cluster overflow at 375px (S37 finding)
 * plus 3 separate places to find user/account actions (signout button + name
 * label + nothing-on-click avatar). The dropdown collapses all of that into a
 * single canonical entry point.
 *
 * Ask Nexus search bar is a stub: click → navigate to /ask. Real Cmd+K palette
 * + inline answers ship in a later sprint.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, LifeBuoy, LogOut, User, Menu } from 'lucide-react';
import { useAppStore } from '@/stores/useAppStore';
import { signOut } from '@/lib/auth';
import { EntitySwitcher } from './EntitySwitcher';
import { NotificationBell } from './NotificationBell';
import { UserDropdown } from './UserDropdown';
import { LensSwitch } from '@/components/home/LensSwitch';
import { Logo } from '@/components/ui/Logo';
import type { Entity } from '@/types';

interface HeaderProps {
  entities: Entity[];
  onOpenNav?: () => void;
}

export function Header({ entities, onOpenNav }: HeaderProps) {
  const { user, logout: clearStore } = useAppStore();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Sign Out stays on the topbar AS WELL AS in the dropdown (per operator
  // 2026-05-28). Icon-only at <sm to fit 375px; label visible at sm+.
  const handleSignOut = () => {
    signOut();
    clearStore();
    window.location.href = '/login';
  };

  // Initials for the avatar; falls back to a User icon if missing.
  const initials = (user?.fullName ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <header
      className="sticky top-0 z-40 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-700"
      data-testid="app-header"
    >
      <div className="flex items-center gap-3 h-16 px-4 lg:px-6 max-w-[1480px] mx-auto">
        {/* Logo + entity switcher. min-w-0 (not shrink-0) so the EntitySwitcher
            label can truncate under pressure rather than push the right cluster
            off-screen at 375px. Tighter gap on mobile. */}
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          {/* Mobile menu (2026-06-02) — opens the left drawer = the desktop
              sidebar. lg:hidden (desktop keeps its sticky rail). Always at the
              top, so the phone browser's bottom toolbar can never cover it. */}
          {onOpenNav && (
            <button
              type="button"
              onClick={onOpenNav}
              aria-label="Open menu"
              data-testid="header-hamburger"
              className="lg:hidden -ml-1 p-2 rounded-[var(--radius-md)] text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors shrink-0"
            >
              <Menu className="w-6 h-6" />
            </button>
          )}
          <a href="/" className="flex items-center gap-2 no-underline shrink-0">
            <Logo className="w-8 h-8" />
            <span className="font-headline text-lg font-semibold text-slate-900 dark:text-white hidden sm:block">
              Nexus
            </span>
          </a>
          <EntitySwitcher entities={entities} />
        </div>

        {/* Company ⇄ You lens (Home Redesign). Owner/admin only — the component
            self-hides for staff. shrink-0 so it never gets squeezed; icon-only
            below sm to fit the 375px header. */}
        <LensSwitch />

        {/* Ask Nexus search bar — center, grows to fill available space.
            Hidden below md so the topbar fits at 375px (S37 lesson). */}
        <button
          type="button"
          onClick={() => navigate('/ask')}
          aria-label="Ask Nexus"
          data-testid="header-ask-nexus"
          className="hidden md:flex flex-1 max-w-2xl items-center gap-2 px-3 py-2 rounded-[var(--radius-md)] bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 hover:bg-slate-200/70 dark:hover:bg-slate-800 transition-colors text-left"
        >
          <Sparkles className="w-4 h-4 text-primary-600 dark:text-primary-400 shrink-0" />
          <span className="flex-1 text-sm text-slate-500 dark:text-slate-400 truncate">
            Ask Nexus anything…
          </span>
          <span className="text-[10px] font-bold tracking-wider rounded px-1.5 py-0.5 bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
            AI
          </span>
          <kbd className="hidden lg:inline text-[10px] font-mono px-1.5 py-0.5 rounded border border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-900">
            ⌘K
          </kbd>
        </button>

        {/* Right cluster — pushed to far right by ml-auto on mobile (no search
            bar) or by the flex-1 search bar on desktop. */}
        <div className="ml-auto flex items-center gap-1 shrink-0">
          {/* Help — opens /help center */}
          <button
            type="button"
            onClick={() => navigate('/help')}
            aria-label="Help & support"
            data-testid="header-help"
            className="p-2 rounded-[var(--radius-md)] hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <LifeBuoy className="w-5 h-5 text-slate-500 dark:text-slate-400" />
          </button>

          {/* Notifications */}
          <NotificationBell />

          {/* Avatar — opens UserDropdown. Wrapper is relative so the dropdown
              positions correctly via absolute right-0 top-full. */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setDropdownOpen((v) => !v)}
              aria-label="Open account menu"
              aria-expanded={dropdownOpen}
              aria-haspopup="menu"
              data-testid="header-avatar"
              className="flex items-center gap-2 px-1.5 py-1 rounded-[var(--radius-md)] hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-primary-600 dark:bg-primary-500 text-white text-xs font-bold flex items-center justify-center">
                {initials || <User className="w-4 h-4" />}
              </div>
            </button>
            <UserDropdown open={dropdownOpen} onClose={() => setDropdownOpen(false)} />
          </div>

          {/* Sign Out — visible on the topbar at sm+ per operator 2026-05-28.
              HIDDEN below sm: at 375px the topbar (logo + EntitySwitcher + Help +
              Bell + Avatar) has no room for it, and it's redundant on mobile —
              the same handler is the last item in the UserDropdown. This is the
              permanent fix for the mobile-overflow gate (the button's ~44px was
              the last straw pushing the right cluster ~50px past 375px). */}
          <button
            type="button"
            onClick={handleSignOut}
            aria-label="Sign Out"
            data-testid="header-signout"
            className="hidden sm:flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-[var(--radius-md)] border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
          >
            <LogOut className="w-4 h-4 text-red-600 dark:text-red-400" />
            <span className="hidden sm:inline text-sm font-medium text-red-600 dark:text-red-400">
              Sign Out
            </span>
          </button>
        </div>
      </div>
    </header>
  );
}
